import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Pencil, Trash2, Plus, Dumbbell, ArrowUp, ArrowDown } from 'lucide-react';
import { toast } from '../../hooks/use-toast';
import ExportButtons from './ExportButtons';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';

const ProficiencySettingManager = () => {
  const [proficiencies, setProficiencies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingProficiency, setEditingProficiency] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [proficiencyToDelete, setProficiencyToDelete] = useState(null);
  const [sortBy, setSortBy] = useState('display_order');
  const [sortDirection, setSortDirection] = useState('asc');

  const [formData, setFormData] = useState({
    name: '',
    icon_url: '',
    display_order: 0,
  });

  const API_BASE = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');

  useEffect(() => {
    fetchProficiencies();
  }, []);

  const fetchProficiencies = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE}/api/admin/proficiency-icons`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setProficiencies(data.proficiency_icons || []);
      }
    } catch (error) {
      console.error('Error fetching proficiencies:', error);
      toast({ title: 'Error', description: 'Failed to load proficiencies', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const handleCreateProficiency = async () => {
    try {
      if (!formData.name || !formData.icon_url) {
        toast({ title: 'Error', description: 'Please fill all required fields', variant: 'destructive' });
        return;
      }

      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE}/api/admin/proficiency-icons`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...formData,
          display_order: parseInt(formData.display_order || 0)
        })
      });

      if (response.ok) {
        toast({ title: 'Success', description: 'Proficiency created successfully' });
        setIsCreateModalOpen(false);
        resetForm();
        fetchProficiencies();
      } else {
        const error = await response.json();
        toast({ title: 'Error', description: error.detail || 'Failed to create proficiency', variant: 'destructive' });
      }
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to create proficiency', variant: 'destructive' });
    }
  };

  const handleUpdateProficiency = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE}/api/admin/proficiency-icons/${editingProficiency.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...formData,
          display_order: parseInt(formData.display_order || 0)
        })
      });

      if (response.ok) {
        toast({ title: 'Success', description: 'Proficiency updated successfully' });
        setIsEditModalOpen(false);
        setEditingProficiency(null);
        resetForm();
        fetchProficiencies();
      } else {
        const error = await response.json();
        toast({ title: 'Error', description: error.detail || 'Failed to update proficiency', variant: 'destructive' });
      }
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to update proficiency', variant: 'destructive' });
    }
  };

  const handleDeleteProficiency = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE}/api/admin/proficiency-icons/${proficiencyToDelete.id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        toast({ title: 'Success', description: 'Proficiency deleted successfully' });
        setDeleteConfirmOpen(false);
        setProficiencyToDelete(null);
        fetchProficiencies();
      }
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to delete proficiency', variant: 'destructive' });
    }
  };

  const openEditModal = (proficiency) => {
    setEditingProficiency(proficiency);
    setFormData({
      name: proficiency.name,
      icon_url: proficiency.icon_url,
      display_order: proficiency.display_order || 0,
    });
    setIsEditModalOpen(true);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      icon_url: '',
      display_order: 0,
    });
  };

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortDirection('asc');
    }
  };

  const sortedProficiencies = [...proficiencies].sort((a, b) => {
    let aVal = a[sortBy];
    let bVal = b[sortBy];
    
    if (sortBy === 'display_order') {
      aVal = parseInt(aVal || 0);
      bVal = parseInt(bVal || 0);
    } else if (typeof aVal === 'string') {
      aVal = aVal.toLowerCase();
      bVal = bVal.toLowerCase();
    }
    
    if (sortDirection === 'asc') {
      return aVal > bVal ? 1 : -1;
    } else {
      return aVal < bVal ? 1 : -1;
    }
  });

  if (loading) {
    return <div className="p-6">Loading proficiencies...</div>;
  }

  return (
    <div className="p-6">
      <div className="mb-6 flex justify-between items-center">
        <div>
          <h2 className="text-3xl font-bold text-gray-900 dark:text-white">Proficiency Setting</h2>
          <p className="text-gray-600 dark:text-gray-400 mt-1">Manage sport specializations and their icons</p>
        </div>
        <Button onClick={() => setIsCreateModalOpen(true)} className="flex items-center gap-2">
          <Plus className="w-4 h-4" />
          Add Proficiency
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <Card>
          <CardContent className="p-4">
            <div className="text-sm text-gray-600 dark:text-gray-400">Total Proficiencies</div>
            <div className="text-3xl font-bold">{proficiencies.length}</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-sm text-gray-600 dark:text-gray-400">Active</div>
            <div className="text-3xl font-bold text-green-600">
              {proficiencies.filter(p => p.active !== false).length}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-sm text-gray-600 dark:text-gray-400">Inactive</div>
            <div className="text-3xl font-bold text-gray-400">
              {proficiencies.filter(p => p.active === false).length}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Export Buttons */}
      <ExportButtons 
        data={sortedProficiencies}
        columns={[
          { header: 'Proficiency ID', key: 'id', accessor: (row) => row.id ? row.id.substring(0, 8) + '...' : 'N/A' },
          { header: 'Name', key: 'name', accessor: (row) => row.name || 'N/A' },
          { header: 'Icon', key: 'icon_url', accessor: (row) => row.icon_url || 'N/A' },
          { header: 'Display Order', key: 'display_order', accessor: (row) => row.display_order || 0 },
          { header: 'Status', key: 'active', accessor: (row) => row.active !== false ? 'Active' : 'Inactive' }
        ]}
        filename="proficiency-settings"
      />

      {/* Proficiencies Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Dumbbell className="w-5 h-5" />
            All Proficiencies
          </CardTitle>
        </CardHeader>
        <CardContent>
          {proficiencies.length === 0 ? (
            <div className="text-center py-12 text-gray-500 dark:text-gray-400">
              <Dumbbell className="w-12 h-12 mx-auto mb-4 text-gray-300" />
              <p>No proficiencies yet. Add your first proficiency!</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 dark:bg-gray-700 border-b">
                  <tr>
                    <th 
                      className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700"
                      onClick={() => handleSort('id')}
                    >
                      <div className="flex items-center gap-2">
                        Proficiency ID
                        {sortBy === 'id' && (
                          sortDirection === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />
                        )}
                      </div>
                    </th>
                    <th 
                      className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700"
                      onClick={() => handleSort('name')}
                    >
                      <div className="flex items-center gap-2">
                        Proficiency Name
                        {sortBy === 'name' && (
                          sortDirection === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />
                        )}
                      </div>
                    </th>
                    <th 
                      className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700"
                      onClick={() => handleSort('icon_url')}
                    >
                      <div className="flex items-center gap-2">
                        Proficiency Icon
                        {sortBy === 'icon_url' && (
                          sortDirection === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />
                        )}
                      </div>
                    </th>
                    <th 
                      className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700"
                      onClick={() => handleSort('display_order')}
                    >
                      <div className="flex items-center gap-2">
                        Order
                        {sortBy === 'display_order' && (
                          sortDirection === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />
                        )}
                      </div>
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {sortedProficiencies.map((proficiency) => (
                    <tr key={proficiency.id} className="hover:bg-gray-50 dark:bg-gray-700">
                      <td className="px-4 py-3 text-sm font-mono text-gray-600 dark:text-gray-400">
                        {proficiency.id.substring(0, 8)}...
                      </td>
                      <td className="px-4 py-3 text-sm font-medium">{proficiency.name}</td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <span className="text-2xl">{proficiency.icon_url}</span>
                          <span className="text-xs text-gray-500 dark:text-gray-400">{proficiency.icon_url}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm">{proficiency.display_order || 0}</td>
                      <td className="px-4 py-3">
                        <Badge variant={proficiency.active !== false ? 'default' : 'secondary'}>
                          {proficiency.active !== false ? 'Active' : 'Inactive'}
                        </Badge>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openEditModal(proficiency)}
                          >
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setProficiencyToDelete(proficiency);
                              setDeleteConfirmOpen(true);
                            }}
                          >
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create/Edit Modal */}
      <Dialog open={isCreateModalOpen || isEditModalOpen} onOpenChange={(open) => {
        if (!open) {
          setIsCreateModalOpen(false);
          setIsEditModalOpen(false);
          setEditingProficiency(null);
          resetForm();
        }
      }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editingProficiency ? 'Edit Proficiency' : 'Add New Proficiency'}</DialogTitle>
            <DialogDescription>
              {editingProficiency ? 'Update proficiency details' : 'Add a new sport specialization'}
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-4 py-4">
            <div>
              <Label>Proficiency Name *</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., Yoga, Boxing, Swimming"
              />
            </div>

            <div>
              <Label>Icon (Emoji) *</Label>
              <Input
                value={formData.icon_url}
                onChange={(e) => setFormData({ ...formData, icon_url: e.target.value })}
                placeholder="e.g., 🧘, 🥊, 🏊"
                className="text-2xl"
              />
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                Enter an emoji to represent this proficiency
              </p>
            </div>

            <div>
              <Label>Display Order</Label>
              <Input
                type="number"
                value={formData.display_order}
                onChange={(e) => setFormData({ ...formData, display_order: e.target.value })}
                placeholder="0"
              />
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                Lower numbers appear first
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setIsCreateModalOpen(false);
              setIsEditModalOpen(false);
              setEditingProficiency(null);
              resetForm();
            }}>
              Cancel
            </Button>
            <Button onClick={editingProficiency ? handleUpdateProficiency : handleCreateProficiency}>
              {editingProficiency ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Proficiency</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete "{proficiencyToDelete?.name}"? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
            <Button variant="destructive" onClick={handleDeleteProficiency}>Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ProficiencySettingManager;
